import { logD, logE } from "@blacknut/logging/dist";
import axios, { AxiosError, AxiosRequestConfig, Method } from "axios";
import { Observable, Subject, Subscriber } from "rxjs";
import {
  ApiError,
  ApiErrorCode,
  ClientDeviceType,
  SupportedOS,
  UserAgent,
  createUserAgentString,
} from "../model";

interface RequestParams {
  method?: Method;
  path: string;
  body?: (() => Record<string, unknown>) | Record<string, unknown>;
  params?: Record<string, string | undefined | number>;
  headers?: Record<string, string>;
}

const DEFAULT_UA: UserAgent = {
  apiVersion: "5.0.2",
  client: ClientDeviceType.WEB,
  clientVersion: "1.0.0",
  deviceType: "DESKTOP",
  os: SupportedOS.Windows,
};
const DEFAULT_ENDPOINT = "https://edito.blacknut.com/api/v1/admin";
export const TAG = "EditoAPI";

class EditoService {
  private _token: string | undefined;
  public get token(): string | undefined {
    return this._token;
  }
  public set token(v: string | undefined) {
    this._token = v;
  }

  private _endpoint = DEFAULT_ENDPOINT;
  public get endpoint(): string {
    return this._endpoint;
  }
  public set endpoint(v: string) {
    this._endpoint = v;
  }

  private _userAgent: UserAgent = { ...DEFAULT_UA };

  public get userAgent(): UserAgent {
    return this._userAgent;
  }
  public set userAgent(v: UserAgent) {
    this._userAgent = v;
  }

  private _locale: string | undefined;
  public get locale(): string | undefined {
    return this._locale;
  }
  public set locale(v: string | undefined) {
    this._locale = v;
  }

  private _onError = new Subject<ApiError>();
  public onError = () => this._onError.asObservable();

  private getDefaultAxiosConfig(
    headers?: Record<string, string>,
    params?: Record<string, string | number | undefined>,
  ): AxiosRequestConfig {
    const _headers: Record<string, string> = {
      "X-Blk-User-Agent": createUserAgentString(this.userAgent),
      "X-Blk-Brand": this.userAgent.flavor || "blacknut",
      "Accept-Language": this.locale || "en-US",
      ...headers,
    };
    if (this.token) {
      _headers["Authorization"] = `Bearer ${this.token}`;
    }

    return {
      headers: _headers,
      params,
      responseType: "json",
    };
  }

  public cleanup() {
    this.token = undefined;
    this.userAgent = { ...DEFAULT_UA };
    this.endpoint = DEFAULT_ENDPOINT;
    this._locale = undefined;
  }

  public get<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "GET",
    });
  }

  protected shouldTriggerGlobalErrorHandler(error: ApiError) {
    return this.isNotAuthenticatedError(error) || this.isObsoleteApiError(error);
  }
  protected isNotAuthenticatedError(error: ApiError): boolean {
    const res: boolean = error.status === "401";
    logD(TAG, "isNotAuthenticatedError %s", res);
    return res;
  }

  protected isObsoleteApiError(error: ApiError): boolean {
    const res: boolean =
      error.status === "406" &&
      (error.code === ApiErrorCode.OBSOLETE_API ||
        error.code === ApiErrorCode.OBSOLETE_CLIENT);
    logD(TAG, "isObsoleteApiError %s", res);
    return res;
  }

  private handleError<T>(
    error: AxiosError<{ errors: ApiError[] }>,
    observer: Subscriber<T>,
  ) {
    logE(TAG, "In handle error: %o", error);
    if (
      error.response?.data &&
      error.response?.data.errors &&
      error.response?.data.errors.length > 0
    ) {
      const _error = error.response?.data.errors[0];

      const wrapped = this.wrapError(_error);
      observer.error(wrapped);
      observer.complete();
      if (this.shouldTriggerGlobalErrorHandler(wrapped)) {
        this._onError.next(wrapped);
      }
    } else {
      const wrapped = this.wrapError(
        this.wrapError({
          code: "unknown",
          status: "",
          title: error.message,
        }),
      );
      observer.error(wrapped);
      observer.complete();
      if (this.shouldTriggerGlobalErrorHandler(wrapped)) {
        this._onError.next(wrapped);
      }
    }
  }

  private wrapError(_error: ApiError) {
    return {
      ..._error,
      toString: () => {
        return _error.title;
      },
    };
  }

  public request<T>({ method, path, body, params, headers }: RequestParams) {
    return new Observable<T>((observer) => {
      logD(TAG, "%o %o", method, path);
      const request = () => {
        // if (path === "v1/config") {
        //   return Promise.reject({
        //     response: {
        //       status: 406,
        //       statusText: "access_token_expired",
        //       data: {
        //         errors: [
        //           {
        //             status: "406",
        //             title: "access_token_expired",
        //             code: ApiErrorCode.ACCESS_TOKEN_EXPIRED,
        //           },
        //         ],
        //       },
        //     },
        //   });
        // }
        return axios
          .request<T>({
            ...this.getDefaultAxiosConfig(headers, params),
            method,
            url: `${this.endpoint}/${path}`,
            data: typeof body === "function" ? body() : body,
          })
          .then((res) => res.data);
      };
      request()
        .then((data) => {
          observer.next(data);
          observer.complete();
        })
        .catch((e) => {
          this.handleError(e, observer);
        });
    });
  }

  public post<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "POST",
    });
  }

  public put<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "PUT",
    });
  }

  public deleteApi<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "DELETE",
    });
  }
}

export const editoService = new EditoService();
export default editoService;
